import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getBlock,
  fetchBlocksByClient,
  linkBlocks,
  unlinkBlock, // Import unlink thunk
} from "../store/thunks/blocksThunk";
import { XMarkIcon } from "@heroicons/react/24/outline";

const LinkBlockModal = ({ isOpen, onClose, blockId }) => {
  const dispatch = useDispatch();
  const [currentBlock, setCurrentBlock] = useState(null);
  const [clientBlocks, setClientBlocks] = useState([]);
  const [selectedPrevious, setSelectedPrevious] = useState("");
  const [selectedNext, setSelectedNext] = useState("");

  // Track whether modal is unexpectedly closing
  useEffect(() => {
    console.log("Modal Open:", isOpen);
  }, [isOpen]);

  // Fetch current block details
  useEffect(() => {
    if (isOpen && blockId) {
      dispatch(getBlock(blockId))
        .unwrap()
        .then((data) => {
          setCurrentBlock(data);
          console.log("Fetched Block Data:", data);
        })
        .catch((err) => console.error("Error fetching current block:", err));
    }
  }, [isOpen, blockId, dispatch]);

  // Fetch client blocks AFTER currentBlock is set
  useEffect(() => {
    if (isOpen && currentBlock?.client) {
      console.log("Fetching blocks for client:", currentBlock.client);
      dispatch(fetchBlocksByClient(currentBlock.client))
        .unwrap()
        .then((data) => {
          setClientBlocks(data);
          console.log("Fetched Client Blocks:", data);
        })
        .catch((err) => console.error("Error fetching client blocks:", err));
    }
  }, [isOpen, currentBlock, dispatch]);

  // Prevent modal from closing unexpectedly by ensuring currentBlock isn't reset
  useEffect(() => {
    if (!isOpen) {
      console.log("Modal is closed. Resetting state.");
      setCurrentBlock(null);
      setClientBlocks([]);
      setSelectedPrevious("");
      setSelectedNext("");
    }
  }, [isOpen]);

  // Link Previous Block
  const handleLinkPrevious = async () => {
    if (!selectedPrevious || !currentBlock) return;
    try {
      await dispatch(
        linkBlocks({ blockAId: selectedPrevious, blockBId: currentBlock._id })
      ).unwrap();
      alert("Previous block linked successfully.");

      // Refetch the current block
      dispatch(getBlock(blockId))
        .unwrap()
        .then((updatedBlock) => setCurrentBlock(updatedBlock));

      setSelectedPrevious("");
    } catch (err) {
      console.error("Error linking previous block:", err);
      alert("Failed to link previous block.");
    }
  };

  // Link Next Block
  const handleLinkNext = async () => {
    if (!selectedNext || !currentBlock) return;
    try {
      await dispatch(
        linkBlocks({ blockAId: currentBlock._id, blockBId: selectedNext })
      ).unwrap();
      alert("Next block linked successfully.");

      // Refetch the current block
      dispatch(getBlock(blockId))
        .unwrap()
        .then((updatedBlock) => setCurrentBlock(updatedBlock));

      setSelectedNext("");
    } catch (err) {
      console.error("Error linking next block:", err);
      alert("Failed to link next block.");
    }
  };

  // Unlink Previous Block
  const handleUnlinkPrevious = async () => {
    if (!currentBlock?.previousBlock) return;
    try {
      await dispatch(
        unlinkBlock({
          blockAId: currentBlock.previousBlock._id,
          blockBId: currentBlock._id,
        })
      ).unwrap();
      alert("Previous block unlinked successfully.");

      // Refetch the current block
      dispatch(getBlock(blockId))
        .unwrap()
        .then((updatedBlock) => setCurrentBlock(updatedBlock));
    } catch (err) {
      console.error("Error unlinking previous block:", err);
      alert("Failed to unlink previous block.");
    }
  };

  // Unlink Next Block
  const handleUnlinkNext = async () => {
    if (!currentBlock?.nextBlock) return;
    try {
      await dispatch(
        unlinkBlock({
          blockAId: currentBlock._id,
          blockBId: currentBlock.nextBlock._id,
        })
      ).unwrap();
      alert("Next block unlinked successfully.");

      // Refetch the current block
      dispatch(getBlock(blockId))
        .unwrap()
        .then((updatedBlock) => setCurrentBlock(updatedBlock));
    } catch (err) {
      console.error("Error unlinking next block:", err);
      alert("Failed to unlink next block.");
    }
  };

  const handleClose = () => {
    console.log("Closing Modal");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-sm w-full max-w-3xl p-4 relative shadow-md">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors">
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Current Block Details */}
        {currentBlock ? (
          <div className="mb-3">
            <h2 className="text-base font-semibold text-gray-900">
              {currentBlock.title || "Untitled Block"}
            </h2>
          </div>
        ) : (
          <p className="mb-4 text-xs text-gray-600">Loading block details...</p>
        )}

        {/* Linking Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Link/Unlink Previous Block */}
          <div>
            <p className="mb-2 text-xs text-gray-600">
              Current Previous: {currentBlock?.previousBlock?.title ?? "None"}
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Link Previous Block
            </label>
            <select
              value={selectedPrevious}
              onChange={(e) => setSelectedPrevious(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20">
              <option value="">Select a block</option>
              {clientBlocks
                .filter((b) => b._id !== currentBlock?._id)
                .map((block) => (
                  <option key={block._id} value={block._id}>
                    {block.title || `Block ${block._id.slice(-5)}`} -{" "}
                    {block.appointments?.length || 0} sessions
                  </option>
                ))}
            </select>
            <button
              onClick={handleLinkPrevious}
              className="mt-2 w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-sm shadow-sm transition-colors disabled:opacity-50"
              disabled={!selectedPrevious}>
              Link as Previous
            </button>

            {/* Unlink Previous Block Button */}
            {currentBlock?.previousBlock && (
              <button
                onClick={handleUnlinkPrevious}
                className="mt-2 w-full px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-medium rounded-sm shadow-sm transition-colors">
                Unlink Previous
              </button>
            )}
          </div>

          {/* Link/Unlink Next Block */}
          <div>
            <p className="mb-2 text-xs text-gray-600">
              Current Next: {currentBlock?.nextBlock?.title ?? "None"}
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Link Next Block
            </label>
            <select
              value={selectedNext}
              onChange={(e) => setSelectedNext(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20">
              <option value="">Select a block</option>
              {clientBlocks
                .filter((b) => b._id !== currentBlock?._id)
                .map((block) => (
                  <option key={block._id} value={block._id}>
                    {block.title || `Block ${block._id.slice(-5)}`} -{" "}
                    {block.appointments?.length || 0} sessions
                  </option>
                ))}
            </select>
            <button
              onClick={handleLinkNext}
              className="mt-2 w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-sm shadow-sm transition-colors disabled:opacity-50"
              disabled={!selectedNext}>
              Link as Next
            </button>

            {/* Unlink Next Block Button */}
            {currentBlock?.nextBlock && (
              <button
                onClick={handleUnlinkNext}
                className="mt-2 w-full px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-medium rounded-sm shadow-sm transition-colors">
                Unlink Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkBlockModal;
